import { useState } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import ModalBottom from '@ui/components/Modal/variants/ModalBottom'
import Input from '@ui/forms/Input'
import { formatVbox, parseVbox } from './vbox'
import ButtonsGrid from '@ui/buttons/ButtonsFixed/ButtonsGrid'

const tNamespace = 'measurement:app.vbox'
const NUMERIC_INPUT_REGEX = /[^0-9.]/g

interface Props {
  open: boolean
  /** Current lens height — prefills the input every time the modal opens. */
  vboxMm?: number
  /** Defaults to `ui:save`; the pre-measurement prompt passes `ui:start`. */
  confirmLabel?: string
  onConfirm: (vboxMm: number) => void
  /** Measure without a lens height. */
  onSkip: () => void
  /**
   * Dismiss, leaving the lens height as it is. Also runs after the closing
   * animation of a confirm or a skip, so it must be idempotent. Defaults to
   * `onSkip` for the pre-measurement prompt, where dismissing *is* skipping.
   */
  close?: () => void
}

export default function Modal({
  open,
  vboxMm,
  confirmLabel,
  onConfirm,
  onSkip,
  close,
}: Props) {
  const { t } = useTranslation(['measurement', 'ui'])
  const [value, setValue] = useState(() => formatVbox(vboxMm))
  const [wasOpen, setWasOpen] = useState(open)
  const nextVboxMm = parseVbox(value)

  // The modal stays mounted between openings, so each opening re-seeds the
  // input from the value the measurement currently runs with rather than
  // keeping what was typed last time.
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setValue(formatVbox(vboxMm))
  }

  const handleChangeText = (text: string) => {
    setValue(text.replace(NUMERIC_INPUT_REGEX, ''))
  }

  const handleConfirm = () => {
    if (nextVboxMm == null) return
    onConfirm(nextVboxMm)
  }

  return (
    <ModalBottom
      compact
      center
      open={open}
      close={close ?? onSkip}
      title={t(`${tNamespace}.header.title`)}
      desc={t(`${tNamespace}.header.desc`)}
      body={
        <View className="flex w-full flex-col gap-8 px-16">
          <Input
            keyboardType="decimal-pad"
            value={value}
            onChangeText={handleChangeText}
            label={t(`${tNamespace}.input.label`)}
            placeholder={t(`${tNamespace}.input.placeholder`)}
          />
          <View className="flex w-full flex-row gap-8">
            <ButtonsGrid
              buttons={[
                {
                  variant: 'outlined',
                  label: t('ui:skip'),
                  onPress: onSkip,
                },
                {
                  variant: 'filled',
                  label: confirmLabel ?? t('ui:apply'),
                  onPress: handleConfirm,
                  disabled: nextVboxMm == null,
                },
              ]}
            />
          </View>
        </View>
      }
    />
  )
}
